'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { useCopy } from '@/app/hooks';
import { normalizePath } from '../../../../_utils';
import { createShare } from '../../../../apis';
import type { ClawFileItem } from '../../../../types';

interface UseShareClawAssetOptions {
  activeClawId?: string;
}

interface ShareClawAssetContext {
  sessionId?: string;
  messageId?: string;
}

export const useShareClawAsset = ({ activeClawId }: UseShareClawAssetOptions) => {
  const t = useTranslations();
  const { copyAsync } = useCopy();
  const [sharingPath, setSharingPath] = useState('');

  const handleShareAsset = useCallback(
    async (item: Pick<ClawFileItem, 'path'>, itemType: 'image' | 'video', context?: ShareClawAssetContext) => {
      const path = normalizePath(item.path);
      if (!path || !activeClawId || sharingPath) return;

      setSharingPath(path);
      const shareUrlPromise = (async () => {
        const { data, error } = await createShare({
          body: {
            claw_id: activeClawId,
            type: itemType,
            path,
            ...(context?.sessionId && context.messageId
              ? {
                  session_id: context.sessionId,
                  message_id: context.messageId,
                }
              : {}),
          },
        });

        if (error || !data?.id) return null;
        return `${window.location.origin}/share/claw-product/${data.id}`;
      })().finally(() => {
        setSharingPath('');
      });

      try {
        await copyAsync(shareUrlPromise, {
          copySuccessMessage: t('claw.chat.share.success'),
          copyFailedMessage: t('claw.chat.share.error'),
          placeholder: 'Generating share link...',
        });
      } catch (error) {
        console.error('Failed to share media asset:', error);
      }
    },
    [activeClawId, copyAsync, sharingPath, t],
  );

  return {
    sharingPath,
    handleShareAsset,
  };
};
